import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, ArrowRight, Eye, Download, RefreshCw } from 'lucide-react';
import { useDocument } from '@hooks/useDocument';
import { useAuth } from '@hooks/useAuth';
import { ROUTES, DOCUMENT_TYPES, DOCUMENT_STATUS } from '@constants';
import Button from '@components/common/Button';
import Card from '@components/common/Card';
import toast from 'react-hot-toast';

const UploadDocumentsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { uploadDocument, getMyDocuments, loading } = useDocument();
  const hasRedirected = useRef(false);
  
  const fileInputRefs = useRef({
    aadhar: null,
    degree_certificate: null,
    experience_certificate: null,
  });
  
  const [documents, setDocuments] = useState({
    aadhar: null,
    degree_certificate: null,
    experience_certificate: null,
  });
  
  
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [manualStep, setManualStep] = useState(null);

  useEffect(() => {
    if (user?.role !== 'mentor') {
      toast.error('Only mentors can upload documents');
      navigate(ROUTES.DASHBOARD);
      return;
    }
    
    fetchDocuments();
  }, [user, navigate]);

  const fetchDocuments = async () => {
    try {
      const response = await getMyDocuments();
      if (response.success) {
        const docs = response.data?.data || response.data || [];
        setUploadedDocs(docs);
      }
    } catch (error) {
      console.error('Failed to fetch documents', error);
    }
  };

  const handleFileChange = (type, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        if (event.target) {
          event.target.value = '';
        }
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPEG, PNG, and PDF files are allowed');
        if (event.target) {
          event.target.value = '';
        }
        return;
      }
      
      setDocuments(prev => ({ ...prev, [type]: file }));
    } else {
      setDocuments(prev => ({ ...prev, [type]: null }));
    }
  };

  const handleUpload = async (type) => {
    const file = documents[type];
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(type);
    try {
      const response = await uploadDocument(type, file);
      if (response.success) {
        toast.success(`${getDocumentLabel(type)} uploaded successfully`);
        setDocuments(prev => ({ ...prev, [type]: null }));
        if (fileInputRefs.current[type]) {
          fileInputRefs.current[type].value = '';
        }
        fetchDocuments();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(null);
    }
  };

  const getDocumentLabel = (type) => {
    const labels = {
      [DOCUMENT_TYPES.AADHAR]: 'Aadhar Card',
      [DOCUMENT_TYPES.DEGREE_CERTIFICATE]: 'Degree Certificate',
      [DOCUMENT_TYPES.EXPERIENCE_CERTIFICATE]: 'Experience Certificate',
    };
    return labels[type] || type;
  };

  const getDocumentDescription = (type) => {
    const descriptions = {
      [DOCUMENT_TYPES.AADHAR]: 'Upload your Aadhar card for identity verification',
      [DOCUMENT_TYPES.DEGREE_CERTIFICATE]: 'Upload your highest degree certificate',
      [DOCUMENT_TYPES.EXPERIENCE_CERTIFICATE]: 'Upload your latest experience/work certificate',
    };
    return descriptions[type] || '';
  };

  const getUploadedDoc = (type) => {
    return uploadedDocs.find(doc => doc.documentType === type);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case DOCUMENT_STATUS.APPROVED:
        return <CheckCircle className="w-5 h-5 text-secondary-600" />;
      case DOCUMENT_STATUS.REJECTED:
        return <XCircle className="w-5 h-5 text-secondary-600" />;
      case DOCUMENT_STATUS.PENDING:
        return <AlertCircle className="w-5 h-5 text-primary-600" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case DOCUMENT_STATUS.APPROVED:
        return 'Approved';
      case DOCUMENT_STATUS.REJECTED:
        return 'Rejected';
      case DOCUMENT_STATUS.PENDING:
        return 'Pending Review';
      default:
        return 'Not Uploaded';
    }
  };

  const renderDocumentUpload = (type) => {
    const uploadedDoc = getUploadedDoc(type);
    const canUpload = !uploadedDoc || uploadedDoc.status === DOCUMENT_STATUS.REJECTED || uploadedDoc.status === DOCUMENT_STATUS.PENDING;

    return (
      <Card key={type} className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-primary-600" />
            <div>
              <h3 className="font-semibold text-primary-700">
                {getDocumentLabel(type)}
              </h3>
              {uploadedDoc && (
                <div className="flex items-center gap-2 mt-1">
                  {getStatusIcon(uploadedDoc.status)}
                  <span className="text-sm text-primary-600">
                    {getStatusText(uploadedDoc.status)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {uploadedDoc?.status === DOCUMENT_STATUS.REJECTED && (
          <div className="mb-4 p-3 bg-secondary-50 rounded-lg">
            <p className="text-sm text-secondary-700 font-medium mb-1">
              Rejection Reason:
            </p>
            <p className="text-sm text-primary-600">
              {uploadedDoc.rejectionReason}
            </p>
          </div>
        )}

        {canUpload && (
          <>
            <p className="text-sm text-primary-600 mb-4">
              {getDocumentDescription(type)}
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Upload File (JPEG, PNG, PDF - Max 5MB)
              </label>
              <input
                ref={(el) => (fileInputRefs.current[type] = el)}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                onChange={(e) => handleFileChange(type, e)}
                className="w-full text-sm text-primary-600
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary-100 file:text-primary-700

                  hover:file:bg-primary-200
                  cursor-pointer"
              />
              {documents[type] && (
                <p className="mt-2 text-sm text-primary-600">
                  Selected: {documents[type].name}
                </p>
              )}
            </div>

            <Button
              onClick={() => handleUpload(type)}
              variant="primary"
              fullWidth
              loading={uploading === type}
              disabled={!documents[type] || uploading === type}
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadedDoc ? 'Reupload Document' : 'Upload Document'}
            </Button>
          </>
        )}

        {uploadedDoc && uploadedDoc.status === DOCUMENT_STATUS.APPROVED && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm text-green-800 font-medium">
                ✓ This document has been approved by admin
              </p>
            </div>
          </div>
        )}
        
        {uploadedDoc && uploadedDoc.status === DOCUMENT_STATUS.PENDING && canUpload && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                ⏳ Document under review. You can change it before approval.
              </p>
            </div>
          </div>
        )}
      </Card>
    );
  };

  // Determine current step based on uploaded documents
  const getDocumentStep = (type) => {
    const order = [DOCUMENT_TYPES.AADHAR, DOCUMENT_TYPES.DEGREE_CERTIFICATE, DOCUMENT_TYPES.EXPERIENCE_CERTIFICATE];
    return order.indexOf(type) + 1;
  };

  const getCurrentStep = () => {
    // If manual step is set, use it
    if (manualStep !== null) return manualStep;
    
    const aadharDoc = getUploadedDoc(DOCUMENT_TYPES.AADHAR);
    const degreeDoc = getUploadedDoc(DOCUMENT_TYPES.DEGREE_CERTIFICATE);
    const experienceDoc = getUploadedDoc(DOCUMENT_TYPES.EXPERIENCE_CERTIFICATE);

    // Check for rejected documents first
    if (aadharDoc?.status === DOCUMENT_STATUS.REJECTED) return 1;
    if (degreeDoc?.status === DOCUMENT_STATUS.REJECTED) return 2;
    if (experienceDoc?.status === DOCUMENT_STATUS.REJECTED) return 3;

    // Otherwise, find the first non-uploaded document
    if (!aadharDoc || aadharDoc.status === DOCUMENT_STATUS.REJECTED) return 1;
    if (!degreeDoc || degreeDoc.status === DOCUMENT_STATUS.REJECTED) return 2;
    if (!experienceDoc || experienceDoc.status === DOCUMENT_STATUS.REJECTED) return 3;
    
    return 4; // All uploaded
  };

  const currentStep = getCurrentStep();
  const totalSteps = 3;

  const getStepStatus = (step) => {
    const docTypes = [DOCUMENT_TYPES.AADHAR, DOCUMENT_TYPES.DEGREE_CERTIFICATE, DOCUMENT_TYPES.EXPERIENCE_CERTIFICATE];
    const doc = getUploadedDoc(docTypes[step - 1]);
    
    if (!doc) return 'pending';
    if (doc.status === DOCUMENT_STATUS.REJECTED) return 'rejected';
    if (doc.status === DOCUMENT_STATUS.APPROVED) return 'approved';
    return 'uploaded';
  };

  const handleStepClick = (step) => {
    const docTypes = [DOCUMENT_TYPES.AADHAR, DOCUMENT_TYPES.DEGREE_CERTIFICATE, DOCUMENT_TYPES.EXPERIENCE_CERTIFICATE];
    const docType = docTypes[step - 1];
    const uploadedDoc = getUploadedDoc(docType);
    
    // Allow clicking if document is pending, rejected, or not uploaded
    // Don't allow clicking if approved
    if (!uploadedDoc || uploadedDoc.status === DOCUMENT_STATUS.PENDING || uploadedDoc.status === DOCUMENT_STATUS.REJECTED) {
      setManualStep(step);
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="mb-3 sm:mb-4">
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          {[1, 2, 3].map((step) => {
            const status = getStepStatus(step);
            const isActive = step === currentStep;
            const isCompleted = status === 'approved' || status === 'uploaded';
            const isRejected = status === 'rejected';
            const isClickable = status !== 'approved';
            
            return (
              <div key={step} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    onClick={() => isClickable && handleStepClick(step)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm sm:text-base font-semibold transition-all ${
                      isCompleted
                        ? 'bg-green-600 text-white'
                        : isRejected
                        ? 'bg-red-600 text-white'
                        : isActive
                        ? 'bg-primary-700 text-white ring-4 ring-primary-200'
                        : 'bg-gray-300 text-gray-600'
                    } ${
                      isClickable ? 'cursor-pointer hover:opacity-80 hover:scale-105' : 'cursor-not-allowed opacity-60'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : isRejected ? (
                      <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      step
                    )}
                  </div>
                  <p className={`mt-1 sm:mt-2 text-xs font-medium ${
                    isActive ? 'text-primary-700' : 'text-gray-600'
                  }`}>
                    Step {step}
                  </p>
                </div>
                {step < 3 && (
                  <div
                    className={`h-0.5 sm:h-1 flex-1 mx-1 sm:mx-2 rounded ${
                      isCompleted ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
        {currentStep <= 3 && (
          <p className="text-xs text-center text-gray-500 mt-1">
            Click on any step to change that document (approved documents cannot be changed)
          </p>
        )}
      </div>
    );
  };

  const renderCurrentStepContent = () => {
    if (currentStep > 3) {
      return (
        <Card className="p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-primary-700 mb-2">
            All Documents Uploaded!
          </h2>
          <p className="text-primary-600 mb-6">
            Your documents are being reviewed by our admin team. This usually takes 24-48 hours.
          </p>
          <Button
            onClick={() => navigate(ROUTES.PROFILE_VERIFICATION)}
            variant="primary"
          >
            View Verification Status
          </Button>
        </Card>
      );
    }

    const docTypes = [DOCUMENT_TYPES.AADHAR, DOCUMENT_TYPES.DEGREE_CERTIFICATE, DOCUMENT_TYPES.EXPERIENCE_CERTIFICATE];
    const currentDocType = docTypes[currentStep - 1];
    const uploadedDoc = getUploadedDoc(currentDocType);
    const canUpload = !uploadedDoc || uploadedDoc.status === DOCUMENT_STATUS.REJECTED || uploadedDoc.status === DOCUMENT_STATUS.PENDING;

    return (
      <Card className="p-3 sm:p-5">
        <div className="mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-bold text-primary-700">
            Step {currentStep} of {totalSteps}: Upload {getDocumentLabel(currentDocType)}
          </h2>
        </div>

        {uploadedDoc?.status === DOCUMENT_STATUS.REJECTED && (
          <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs sm:text-sm font-semibold text-red-800">
                  Rejected: {uploadedDoc.rejectionReason || 'Please upload a valid document.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {canUpload ? (
          <>
            {uploadedDoc && (
              <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <div>
                      <p className="text-xs sm:text-sm font-semibold text-blue-900">
                        Currently Uploaded Document
                      </p>
                      <p className="text-xs text-blue-700">
                        Uploaded on {new Date(uploadedDoc.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {uploadedDoc.status === DOCUMENT_STATUS.PENDING && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                        Under Review
                      </span>
                    )}
                    {uploadedDoc.status === DOCUMENT_STATUS.REJECTED && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                        Rejected
                      </span>
                    )}
                  </div>
                </div>
                
                {uploadedDoc.filePath && (
                  <div className="flex gap-2">
                    <a
                      href={uploadedDoc.filePath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                    >
                      <Eye className="w-4 h-4" />
                      View Document
                    </a>
                    <a
                      href={uploadedDoc.filePath}
                      download
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                )}
                
                {uploadedDoc.status === DOCUMENT_STATUS.REJECTED && uploadedDoc.rejectionReason && (
                  <div className="mt-3 p-3 bg-red-50 rounded border border-red-200">
                    <p className="text-xs font-semibold text-red-800 mb-1">Rejection Reason:</p>
                    <p className="text-xs text-red-700">{uploadedDoc.rejectionReason}</p>
                  </div>
                )}
              </div>
            )}

            <div className="mb-3 sm:mb-4">
              <label className="block text-xs sm:text-sm font-medium text-primary-700 mb-1">
                Upload New File to Replace (JPEG, PNG, PDF - Max 5MB)
              </label>
              <input
                ref={(el) => (fileInputRefs.current[currentDocType] = el)}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                onChange={(e) => handleFileChange(currentDocType, e)}
                className="w-full text-xs sm:text-sm text-primary-600
                  file:mr-2 sm:file:mr-4 file:py-2 sm:file:py-3 file:px-3 sm:file:px-6
                  file:rounded-lg file:border-0
                  file:text-xs sm:file:text-sm file:font-semibold
                  file:bg-primary-100 file:text-primary-700

                  hover:file:bg-primary-200
                  cursor-pointer"
              />
              {documents[currentDocType] && (
                <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs text-green-800 font-medium">
                    <FileText className="w-3 h-3 inline mr-1" />
                    {documents[currentDocType].name}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => handleUpload(currentDocType)}
                variant="primary"
                fullWidth
                loading={uploading === currentDocType}
                disabled={!documents[currentDocType] || uploading === currentDocType}
                className="py-2 text-sm"
              >
                {uploadedDoc ? (
                  <>
                    <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Replace Document
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Upload & Continue
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
            
            {uploadedDoc && uploadedDoc.status === DOCUMENT_STATUS.PENDING && (
              <div className="mt-3">
                <Button
                  onClick={() => setManualStep(null)}
                  variant="outline"
                  fullWidth
                  className="py-2 text-sm"
                >
                  Back to Overview
                </Button>
              </div>
            )}
          </>
        ) : uploadedDoc.status === DOCUMENT_STATUS.APPROVED ? (
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-xs sm:text-sm font-semibold text-green-800">
                Document Approved
              </p>
            </div>
          </div>
        ) : null}
      </Card>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Blurred Background Overlay */}
      <div className="fixed inset-0 bg-primary-900/80 backdrop-blur-md" />
      
      {/* Modal Content */}
      <div className="relative min-h-screen flex items-center justify-center p-2 sm:p-4">
        <div className="relative w-full max-w-3xl">
          {/* Lock Icon Badge */}
          <div className="absolute -top-8 sm:-top-12 left-1/2 transform -translate-x-1/2">
            <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-full p-2 sm:p-3 shadow-2xl">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>

          {/* Main Card */}
          <Card className="bg-white shadow-2xl border border-primary-200">
            <div className="p-3 sm:p-5">
              <div className="text-center mb-3">
                <h1 className="text-lg sm:text-xl font-bold text-primary-700">
                  Complete Your Profile
                </h1>
                <p className="text-xs sm:text-sm text-primary-600 mt-1">
                  Upload your verification documents to become a verified mentor
                </p>
              </div>

              {renderStepIndicator()}
              {renderCurrentStepContent()}

              <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> All documents will be reviewed by our admin team within 24-48 hours.
                </p>
              </div>

              {/* Cannot Skip Message */}
              <div className="mt-3 sm:mt-4 text-center">
                <p className="text-xs text-primary-500 flex items-center justify-center gap-2">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  You must complete profile verification to access the platform
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentsPage;
